import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobArchitectureMetadata } from '~/shared/data-contract/job-architecture';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseUpdateJobArchitectureMetadataResult = UseMutationResult<
    unknown,
    Error,
    {
        readonly jobArchitectureId: string;
        readonly metadata: Partial<JobArchitectureMetadata>;
        readonly allowJsaRename?: boolean;
    }
>;

export const useUpdateJobArchitectureMetadata = (): UseUpdateJobArchitectureMetadataResult => {
    const { patchJobArchitectureMetadata } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobArchitectureId, metadata, allowJsaRename }) => {
            const result = await patchJobArchitectureMetadata({
                jobArchitectureId,
                metadata,
                allowJsaRename,
            });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.metadata(variables.jobArchitectureId),
            });

            if (variables.allowJsaRename === true) {
                queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architectures() });
                queryClient.invalidateQueries({
                    queryKey: jobArchitectureKeys.architectureDetail(variables.jobArchitectureId),
                });
            }
        },
    });
};
